import { startTransition, useEffect, useMemo, useState } from 'react'

import type { InfiniteData } from '@tanstack/react-query'

import {
  type PaginatedPost,
  Post,
  PostListFilterByEnum,
  PostListFilterByOperator,
  PostListOrderByEnum,
} from '@tribeplatform/gql-client/types'
import { useAuthMember, useFeed } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { useBlockSsrInitialData } from '@tribeplatform/react-slate-kit/hooks'

import { useQuery } from '../common/hooks/useQuery.js'
import { GenericPostList } from '../PostList/GenericPostList.js'
import { PostListFilter } from '../PostList/PostListFilter.js'
import { getUserSettings, setUserSetting } from '../utils/userSettings/index.js'

import { EmptyState } from './EmptyState.js'
import { TruncateSettingsContext } from './TruncateSettingsProvider.js'
import { FeedProps, TruncateSettings } from './types.js'

export const Feed = ({
  view = 'card',
  orderBy: defaultOrderBy = PostListOrderByEnum.publishedAt,
  truncateSize = 'md',
  displayStyle = 'expandable',
  ...rest
}: FeedProps) => {
  const { isGuest } = useAuthMember()
  const ssrInitial = useBlockSsrInitialData<InfiniteData<PaginatedPost>>()

  /**
   * Default `orderBy` synchronously rather than waiting for the localStorage
   * lookup useEffect — SSR has no localStorage, and leaving `orderBy`
   * undefined on the first render keeps `useFeed` disabled, which leaves
   * `isFetched: false` and forces `GenericPostList` to show its loading
   * skeleton instead of the SSR-prefetched posts. The user-setting override
   * still kicks in below for return visitors on the client.
   */
  const [orderBy, setOrderBy] = useState<PostListOrderByEnum>(defaultOrderBy)

  useEffect(() => {
    const userSettingOrderBy = getUserSettings()?.postListOrderBy?.Feed
    if (userSettingOrderBy && userSettingOrderBy !== orderBy) {
      // `startTransition` marks the update as low-priority so React lets
      // in-flight hydration complete before re-rendering with the new
      // `orderBy`. Without this, the re-render fires inside the hydration
      // window and the surrounding Suspense boundary bails to CSR with a
      // "received an update before it finished hydrating" error.
      startTransition(() => {
        setOrderBy(userSettingOrderBy)
      })
    }
    // Run once on mount — `orderBy` intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { tag_id: tagId } = useQuery()
  const filterBy = []
  if (tagId) {
    filterBy.push({
      key: PostListFilterByEnum.publishedAt,
      keyString: 'tagIds',
      operator: PostListFilterByOperator.contains,
      value: JSON.stringify(tagId),
    })
  }

  useEffect(() => {
    if (orderBy) {
      setUserSetting('postListOrderBy', {
        Feed: orderBy,
      })
    }
  }, [orderBy])

  const {
    data,
    fetchNextPage = false,
    hasNextPage,
    isLoading,
    isFetched,
    isFetchingNextPage,
  } = useFeed({
    fields: {
      tags: 'basic',
      owner: {
        member: { profilePicture: 'basic', badges: 'all' },
      },
      embeds: 'default',
      mentions: 'basic',
      space: 'basic',
      attachments: 'default',
      authMemberProps: 'all',
      reactions: { fields: 'all', variables: { limit: 10 } },
      replies: {
        fields: {
          authMemberProps: 'all',
          embeds: 'default',
          mentions: 'basic',
          attachments: 'default',
          owner: {
            member: { profilePicture: 'basic', badges: 'all' },
          },
          reactions: { fields: 'all', variables: { limit: 10 } },
        },
        variables: {
          limit: 2,
          reverse: true,
        },
      },
    },
    variables: {
      limit: view === 'card' ? 5 : 10,
      onlyMemberSpaces: !isGuest,
      orderBy,
      filterBy,
    },
    useInfiniteQueryOptions: {
      // SSR-injected payload carries the server-stamped `prefetchedAt`.
      // Pair it with `initialData` and a local `staleTime` so react-query
      // honors the freshness window and skips the redundant mount refetch.
      // Without the timestamp, react-query treats `initialData` as
      // epoch-old and refetches on mount.
      //
      // Note: `prefetchedAt` is server-epoch; react-query compares it to
      // client `Date.now()`. NTP-aligned machines drift by seconds, well
      // inside the 30 s window; >30 s drift just causes an extra refetch
      // (no correctness impact).
      staleTime: 30_000,
      ...(ssrInitial
        ? {
            initialData: {
              pages: ssrInitial.pages,
              pageParams: ssrInitial.pageParams,
            },
            initialDataUpdatedAt: ssrInitial.prefetchedAt,
          }
        : {}),
      enabled: !!orderBy,
    },
  })
  const { nodes: posts } = simplifyPaginatedResult<Post>(data)

  const feedTruncateSettings = useMemo<TruncateSettings>(() => {
    return { displayStyle, truncateSize }
  }, [displayStyle, truncateSize])

  return (
    <TruncateSettingsContext.Provider value={feedTruncateSettings}>
      <div {...rest}>
        <PostListFilter orderBy={orderBy} setOrderBy={setOrderBy} />
        <GenericPostList
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          posts={posts}
          isFetched={isFetched}
          view={view}
          emptyState={<EmptyState />}
          context="network"
        />
      </div>
    </TruncateSettingsContext.Provider>
  )
}
